import { DepressionContentPage } from "@/components/DepressionContentPage";
import type { FAQItem } from "@/components/DepressionContentPage";

const faqs: FAQItem[] = [
  {
    question: "Why am I exhausted but unable to sleep?",
    answer:
      "When the body is tired but the mind stays activated, racing thoughts and worry can keep you awake. This can happen with anxiety and is one reason sleep and mental health are often evaluated together.",
  },
  {
    question: "Can anxiety cause racing thoughts at night?",
    answer:
      "Yes. Anxiety commonly shows up as racing thoughts, worry about the next day, or difficulty relaxing, which can make falling asleep harder.",
  },
  {
    question: "Can insomnia affect mood?",
    answer:
      "Poor sleep can affect energy, concentration, mood, and motivation. Over time, sleep disruption may contribute to how someone feels during the day.",
  },
  {
    question: "When should I talk to a mental-health professional?",
    answer:
      "If sleep difficulties, racing thoughts, or anxiety persist, worsen, or begin interfering with daily functioning, an evaluation can help clarify what may be contributing and what options may be appropriate.",
  },
  {
    question: "Does TMS treat insomnia?",
    answer:
      "TMS is not a treatment for insomnia itself. TMS candidacy and safety are determined individually, and sleep concerns should be discussed with the treating clinician.",
  },
  {
    question: "Can someone receive TMS if they are not sleeping well?",
    answer:
      "Sleep concerns do not automatically disqualify someone, but they are part of the clinical picture. A clinician evaluates the whole situation before determining whether TMS is appropriate.",
  },
  {
    question: "What should I do if my symptoms feel overwhelming?",
    answer:
      "If you are in crisis or having thoughts of suicide, call or text 988 (Suicide & Crisis Lifeline, available 24/7), call 911, or go to your nearest emergency room. For ongoing symptoms, a mental-health evaluation can help you understand next steps.",
  },
];

export default function BrainWontShutOff() {
  return (
    <DepressionContentPage
      seo={{
        title: "Why Won't My Brain Shut Off at Night?",
        description:
          "Racing thoughts, anxiety and poor sleep can affect mood, energy and concentration. Learn when it may be time to seek professional help.",
        canonical: "/brain-wont-shut-off",
        keywords:
          "brain won't shut off, racing thoughts at night, anxiety and sleep, insomnia and mood, mental health evaluation",
      }}
      badge="Mental Health Education"
      heroTitle="“My Brain Won't Shut Off at Night”"
      heroSubtitle="When your body is exhausted but your thoughts keep going, anxiety and sleep disruption can begin affecting much more than bedtime."
      heroCtaPrimary="Check My Insurance Options"
      heroCtaPrimaryHref="/check-my-insurance-options"
      sections={[
        {
          heading: "What People Mean When They Say Their Brain Won't Turn Off",
          variant: "text",
          body: (
            <>
              <p>
                It rarely means thinking about one clear thing. More often it is
                a stream: mentally replaying conversations from the day,
                worrying about tomorrow, jumping between thoughts, and feeling
                unable to relax. Over time, some people become anxious about the
                act of falling asleep itself, which only makes it harder.
              </p>
              <p>
                The common thread is feeling tired but mentally activated,
                exhausted but unable to let go.
              </p>
            </>
          ),
        },
        {
          heading: "The Anxiety–Sleep Cycle",
          variant: "steps",
          steps: [
            {
              title: "Worry or Hyperarousal",
              body: "Anxiety keeps the mind alert, making it hard to wind down.",
            },
            {
              title: "Difficulty Sleeping",
              body: "Racing thoughts and tension interfere with falling or staying asleep.",
            },
            {
              title: "Physical and Mental Exhaustion",
              body: "Poor sleep accumulates, draining energy, focus, and resilience.",
            },
            {
              title: "More Worry About How You Feel",
              body: "Feeling worse during the day can increase nighttime worry, restarting the cycle.",
            },
          ],
        },
        {
          heading: "When Sleep Problems Start Affecting Daytime Life",
          variant: "text",
          body: (
            <p>
              Sleep disruption can show up far beyond bedtime. It may affect
              focus, energy, motivation, socializing, work performance,
              emotional resilience, enjoyment, and appetite. When these changes
              add up, they can begin to feel like a larger problem, even if
              sleep is where it started.
            </p>
          ),
        },
        {
          heading: "When Should You Consider an Evaluation?",
          variant: "text",
          body: (
            <>
              <p>
                Occasional restless nights are normal. But when racing thoughts,
                anxiety, or poor sleep persist, worsen, or begin interfering
                with how you function during the day, it may be time to talk
                with a professional.
              </p>
              <p>
                If you are having thoughts of suicide or feel you may be in
                danger, call or text 988 (Suicide & Crisis Lifeline, available
                24/7), call 911, or go to your nearest emergency room.
              </p>
            </>
          ),
        },
        {
          heading: "Treatment Depends on What's Driving the Symptoms",
          variant: "text",
          body: (
            <>
              <p>
                Different people may need different combinations of support. An
                evaluation can help determine what may be contributing and which
                options may be appropriate, which may include:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Psychiatric evaluation</li>
                <li>Medication management</li>
                <li>Psychotherapy</li>
                <li>Sleep-focused care and behavioral strategies</li>
                <li>Neuromodulation where clinically appropriate</li>
              </ul>
              <p>
                No specific medication is recommended here. Treatment decisions
                are made individually with a clinician.
              </p>
            </>
          ),
        },
      ]}
      faq={faqs}
      faqHeading="Frequently Asked Questions"
      relatedLinks={[
        {
          title: "When Anxiety, Insomnia and Depression Overlap",
          href: "/depression-anxiety-insomnia",
        },
        {
          title: "When a Panic Attack Changes Everything",
          href: "/panic-anxiety-depression",
        },
        {
          title: "What Happens During a TMS Consultation?",
          href: "/what-to-expect-from-your-consultation",
        },
        {
          title: "Explore More Mental Health Education",
          href: "/mental-health-resources",
        },
      ]}
      finalCtaHeading="You Don't Have to Sort This Out Alone"
      finalCtaText="Understanding what's driving racing thoughts and poor sleep starts with a conversation. Our team can help you make sense of the pattern."
      finalCtaPrimaryLabel="Check My Insurance Options"
      finalCtaPrimaryHref="/check-my-insurance-options"
      finalCtaSecondaryLabel="Schedule a Private Consultation"
      finalCtaSecondaryHref="/contactus"
    />
  );
}
